import { useEffect, useRef } from 'react';
import { Outlet } from "react-router-dom";
import { hasAuthParams, useAuth } from 'react-oidc-context';

const SESSION_ENDED_ERRORS = new Set([
    "login_required",
    "invalid_grant",
    "invalid_token",
    "session_expired",
    "access_denied",
]);

const TRANSIENT_ERROR_PATTERNS = [
    "network",
    "timeout",
    "timed out",
    "failed to fetch",
    "load failed",
    "no matching state",
];

function isSessionEnded(error: unknown): boolean {
    const err = error as { name?: string; error?: string };
    return err?.name === "ErrorResponse" && !!err.error && SESSION_ENDED_ERRORS.has(err.error);
}

function isTransient(error: unknown): boolean {
    const err = error as { name?: string; message?: string };
    if (err?.name === "ErrorTimeout") {
        return true;
    }
    const message = (err?.message ?? "").toLowerCase();
    return TRANSIENT_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

export default function ProtectedRoute(props: any) {
    const auth = useAuth();
    const hasTriedSignin = useRef(false);
    const handledError = useRef<unknown>(null);
    const silentRetries = useRef(0);
    const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (retryTimer.current !== null) {
                clearTimeout(retryTimer.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!(hasAuthParams() || auth.isAuthenticated || auth.activeNavigator || auth.isLoading || hasTriedSignin.current)) {
            hasTriedSignin.current = true;
            auth.signinSilent();
        }
    }, [auth, auth.events, auth.signinSilent, auth.signinRedirect, hasTriedSignin]);

    useEffect(() => {
        if (!auth.error) {
            silentRetries.current = 0;
            return;
        }
        if (handledError.current === auth.error) {
            return;
        }
        handledError.current = auth.error;

        if (hasAuthParams()) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }

        if (isSessionEnded(auth.error)) {
            hasTriedSignin.current = true;
            auth.signinRedirect({ prompt: 'login' });
            return;
        }

        if (isTransient(auth.error)) {
            if (silentRetries.current < 2) {
                silentRetries.current += 1;
                hasTriedSignin.current = true;
                if (retryTimer.current !== null) {
                    clearTimeout(retryTimer.current);
                }
                retryTimer.current = setTimeout(() => {
                    retryTimer.current = null;
                    auth.signinSilent();
                }, 2000 * silentRetries.current);
            }
            return;
        }

        if (!hasTriedSignin.current) {
            hasTriedSignin.current = true;
            auth.signinSilent();
        }
    }, [auth, auth.error, auth.signinSilent, auth.signinRedirect]);

    const signInButtons = (
        <>
            <button onClick={() => {
                handledError.current = null;
                silentRetries.current = 0;
                hasTriedSignin.current = true;
                auth.signinSilent();
            }}>Try again</button>
            <button onClick={() => auth.signinRedirect()}>Log in</button>
        </>
    );

    if (auth.activeNavigator === "signinRedirect" || (auth.isLoading && !auth.user)) {
        return ( <> <h1>Loading...</h1> </>)
    }
    else if (auth.error && (isSessionEnded(auth.error) || !auth.user)) {
        return (
            <>
                <h1>Error</h1>
                <div>{auth.error?.message}</div>
                {signInButtons}
            </>
        )
    }
    else if (auth.user) {
        return (<Outlet />)
    }
    else if (auth.isLoading || auth.activeNavigator === "signinSilent") {
        return ( <> <h1>Signing in...</h1> </>)
    }
    else {
        return (
            <>
                <h1>We've hit a snag</h1>
                <div>Unable to sign in</div>
                {signInButtons}
            </>
        )
    }
};
